import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'

import { Transition, TransitionChild } from '@headlessui/react'
import { useClickAway } from 'react-use'

type ModalProps = {
	children: ReactNode
	isOpen: boolean
	closeModal: () => void
	desktopStyle: 'full-screen' | 'floating'
	afterClose?: () => void
	closeModalText?: string | null
	ariaLabel?: string
}

export default function Modal({
	isOpen,
	closeModal,
	children,
	desktopStyle,
	afterClose,
	closeModalText = null,
	ariaLabel = 'Dialog'
}: ModalProps) {
	const modalRef = useRef<HTMLDivElement>(null)

	// Close modal when clicked outside
	useClickAway(modalRef, (event) => {
		const onwardModal = document.getElementById('onward-modal')
		if (!onwardModal) {
			closeModal()
		}
	})

	// Keyboard event listener to close modal
	useEffect(() => {
		const closeCurrentModal = (e: KeyboardEvent) => (e.key === 'Escape' ? closeModal() : null)

		if (isOpen) {
			window.addEventListener('keydown', closeCurrentModal)
			document.body.style.overflowY = 'hidden'
		}

		return () => {
			window.removeEventListener('keydown', closeCurrentModal)
			document.body.style.overflowY = 'visible'
		}
	}, [isOpen])

	return (
		<Transition show={isOpen}>
			<div
				className={`fixed inset-0 z-[100] ${
					desktopStyle === 'floating'
						? 'flex items-start justify-center overflow-y-auto px-4 py-8 sm:px-8'
						: 'lg:top-6'
				}`}
			>
				<div ref={modalRef}>
					<TransitionChild
						as="div"
						appear
						enter="transition ease-in-out duration-500"
						enterFrom={desktopStyle === 'floating' ? 'opacity-0' : 'translate-y-full'}
						enterTo={desktopStyle === 'floating' ? 'opacity-100' : 'translate-y-0'}
						leave="transition ease-in-out duration-500"
						leaveFrom={desktopStyle === 'floating' ? 'opacity-100' : 'translate-y-0'}
						leaveTo={desktopStyle === 'floating' ? 'opacity-0' : 'translate-y-full'}
						afterLeave={afterClose}
						className={`flex flex-col bg-white rounded ${
							desktopStyle === 'floating'
								? 'relative w-full my-auto sm:min-w-[428px] sm:w-auto'
								: 'relative h-screen overflow-y-auto'
						}`}
						role="dialog"
						aria-modal="true"
						aria-label={ariaLabel}
					>
						<button
							className="absolute p-1 text-gray-500 top-4 right-4 z-[110]"
							type="button"
							onClick={closeModal}
							aria-label="Close dialog"
						>
							{!closeModalText ? <i className="w-3 fa-light fa-xmark" /> : closeModalText}
						</button>
						{children}
					</TransitionChild>
				</div>
			</div>

			{/* Backdrop */}
			<TransitionChild
				as="div"
				appear
				enter="transition ease-in-out duration-500"
				enterFrom="opacity-0"
				enterTo="opacity-100"
				leave="transition ease-in-out duration-500"
				leaveFrom="opacity-100"
				leaveTo="opacity-0"
				className="fixed inset-0 z-[90] bg-bbx-style/50"
				aria-hidden="true"
			/>
		</Transition>
	)
}
